import React, { useState, useEffect } from 'react';
import { Client, BannerSet, BannerSize } from '../types';
import { ArrowLeft, Eye, Edit, Calendar, Activity, Grid, Download, Play, Plus, Zap, Globe } from 'lucide-react';
import { PreviewModal } from './PreviewModal';
import { BannerSetPreviewModal } from './BannerSetPreviewModal';
import { BannerSetExportModal } from './BannerSetExportModal';
import { PlatformExportModal } from './PlatformExportModal';
import { BannerDeploymentModal } from './BannerDeploymentModal';
import { AddBannerSizeModal } from './AddBannerSizeModal';

interface BannerSetEditorProps {
  client: Client;
  bannerSet: BannerSet;
  onNavigateToBannerEditor: (clientId: string, bannerSetId: string, sizeId: string) => void;
  onUpdateBannerSet: (updates: Partial<BannerSet>) => void;
  onBack: () => void;
}

export const BannerSetEditor: React.FC<BannerSetEditorProps> = ({
  client,
  bannerSet,
  onNavigateToBannerEditor,
  onUpdateBannerSet,
  onBack
}) => {
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [showBannerSetPreview, setShowBannerSetPreview] = useState(false);
  const [showBannerSetExport, setShowBannerSetExport] = useState(false);
  const [showPlatformExport, setShowPlatformExport] = useState(false);
  const [showDeploymentModal, setShowDeploymentModal] = useState(false);
  const [showAddBannerSize, setShowAddBannerSize] = useState(false);
  const [previewSize, setPreviewSize] = useState<BannerSize | null>(null);

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  const getCompletionStatus = (size: BannerSize) => {
    return size.elements.length > 0 ? 'completed' : 'empty';
  };

  const getStatusColor = (status: string) => {
    return status === 'completed' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600';
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'square': return '⬜';
      case 'horizontal': return '▬';
      case 'vertical': return '▮';
      default: return '⬜';
    }
  };

  const handlePreviewSize = (size: BannerSize) => {
    if (size.elements.length === 0) {
      alert('This banner size has no elements to preview. Please add elements first.');
      return;
    }
    setPreviewSize(size);
    setShowPreviewModal(true);
  };

  const handlePreviewAll = () => {
    const completedSizes = bannerSet.sizes.filter(size => size.elements.length > 0);
    if (completedSizes.length === 0) {
      alert('No completed banner sizes to preview. Please design at least one banner first.');
      return;
    }
    
    setShowBannerSetPreview(true);
  };

  const handleExportAll = () => {
    const completedSizes = bannerSet.sizes.filter(size => size.elements.length > 0);
    if (completedSizes.length === 0) {
      alert('No completed banner sizes to export. Please design at least one banner first.');
      return;
    }
    
    setShowBannerSetExport(true);
  };

  const handlePlatformExport = () => {
    const completedSizes = bannerSet.sizes.filter(size => size.elements.length > 0);
    if (completedSizes.length === 0) {
      alert('No completed banner sizes to export. Please design at least one banner first.');
      return;
    }
    
    setShowPlatformExport(true);
  };

  const handleDeployment = () => {
    const completedSizes = bannerSet.sizes.filter(size => size.elements.length > 0);
    if (completedSizes.length === 0) {
      alert('No completed banner sizes to deploy. Please design at least one banner first.');
      return;
    }
    
    setShowDeploymentModal(true);
  };

  const handleAddBannerSize = (newBannerSize: BannerSize) => {
    const updatedSizes = [...bannerSet.sizes, newBannerSize];
    onUpdateBannerSet({ 
      sizes: updatedSizes,
      lastModified: new Date()
    });
  };

  const groupedSizes = bannerSet.sizes.reduce((groups, size) => {
    if (!groups[size.category]) {
      groups[size.category] = [];
    }
    groups[size.category].push(size);
    return groups;
  }, {} as Record<string, BannerSize[]>);

  const hasCompletedSizes = bannerSet.sizes.some(size => size.elements.length > 0);
  const hasDataFeed = bannerSet.dataFeedArray.length > 0;

  return (
    <div className="h-full overflow-auto">
      <div className="max-w-7xl mx-auto p-6">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-4">
              <button
                onClick={onBack}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ArrowLeft size={20} />
              </button>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">{bannerSet.name}</h1>
                <p className="text-gray-600 mt-1">{bannerSet.description}</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-3">
              {/* Add Banner Size Button */}
              <button
                onClick={() => setShowAddBannerSize(true)}
                className="flex items-center space-x-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
              >
                <Plus size={16} />
                <span>Add Banner Size</span>
              </button>

              {/* Preview All Button - Show when there are completed sizes */}
              {hasCompletedSizes && (
                <button
                  onClick={handlePreviewAll}
                  className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <Eye size={16} />
                  <span>Preview</span>
                </button>
              )}

              {/* Deploy Button - Show when there are completed sizes and data feed */}
              {hasCompletedSizes && hasDataFeed && (
                <button
                  onClick={handleDeployment}
                  className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                >
                  <Globe size={16} />
                  <span>Deploy</span>
                </button>
              )}

              {/* Platform Export Button - Show when there are completed sizes */}
              {hasCompletedSizes && (
                <button
                  onClick={handlePlatformExport}
                  className="flex items-center space-x-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
                >
                  <Zap size={16} />
                  <span>Publish</span>
                </button>
              )}
              
              {/* Export All Button - Show when there are completed sizes */}
              {hasCompletedSizes && (
                <button
                  onClick={handleExportAll}
                  className="flex items-center space-x-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                >
                  <Download size={16} />
                  <span>Export</span>
                </button>
              )}
              
              <span className={`px-3 py-1 text-sm font-medium rounded-full ${
                bannerSet.status === 'active' ? 'bg-green-100 text-green-800' :
                bannerSet.status === 'draft' ? 'bg-yellow-100 text-yellow-800' :
                'bg-gray-100 text-gray-800'
              }`}>
                {bannerSet.status}
              </span>
            </div>
          </div>

          {/* Banner Set Info */}
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div>
                <p className="text-sm font-medium text-gray-600">Client</p>
                <p className="text-lg text-gray-900">{client.name}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600">Total Sizes</p>
                <p className="text-lg text-gray-900">{bannerSet.sizes.length}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600">Completed</p>
                <p className="text-lg text-gray-900">
                  {bannerSet.sizes.filter(size => size.elements.length > 0).length}
                </p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600">Data Items</p>
                <p className="text-lg text-gray-900">
                  {bannerSet.dataFeedArray.length || 'None'}
                </p>
              </div>
            </div>
          </div>

          {/* Data Feed Status */}
          {bannerSet.dataFeedArray.length > 0 ? (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
              <div className="flex items-center space-x-2 mb-2">
                <Activity className="h-5 w-5 text-green-600" />
                <span className="font-medium text-green-800">Data Feed Connected</span>
              </div>
              <p className="text-sm text-green-700">
                {bannerSet.dataFeedArray.length} data items loaded. 
                Preview, export, deployment, and platform publishing options are available for all banner sizes.
              </p>
            </div>
          ) : (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
              <div className="flex items-center space-x-2 mb-2">
                <Calendar className="h-5 w-5 text-yellow-600" />
                <span className="font-medium text-yellow-800">No Data Feed</span>
              </div>
              <p className="text-sm text-yellow-700">
                Upload a JSON data feed to enable preview, batch export, deployment, and platform publishing functionality.
                {hasCompletedSizes && (
                  <span className="block mt-1 font-medium">
                    You can still preview individual banner sizes without a data feed.
                  </span>
                )}
              </p>
            </div>
          )}
        </div>

        {/* Banner Sizes by Category */}
        {Object.entries(groupedSizes).map(([category, sizes]) => (
          <div key={category} className="mb-8">
            <div className="flex items-center space-x-2 mb-4">
              <span className="text-2xl">{getCategoryIcon(category)}</span>
              <h2 className="text-xl font-semibold text-gray-900 capitalize">{category} Banners</h2>
              <span className="text-sm text-gray-500">({sizes.length} sizes)</span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {sizes.map(size => {
                return (
                  <div
                    key={size.id}
                    className="bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow"
                  >
                    <div className="p-4">
                      {/* Size Preview - Clean empty state */}
                      <div className="mb-4">
                        <div 
                          className="w-full bg-gray-100 rounded flex items-center justify-center relative overflow-hidden border border-gray-200"
                          style={{ 
                            aspectRatio: `${size.width}/${size.height}`,
                            minHeight: '120px',
                            maxHeight: '200px'
                          }}
                        >
                          {size.elements.length > 0 && size.thumbnail ? (
                            <img
                              src={size.thumbnail}
                              alt={`${size.name} thumbnail`}
                              className="w-full h-full object-cover"
                              style={{ imageRendering: 'crisp-edges' }}
                            />
                          ) : (
                            <div className="text-center">
                              <Grid className="mx-auto h-8 w-8 text-gray-400 mb-2" />
                              <p className="text-xs text-gray-500">
                                {size.elements.length > 0 ? `${size.elements.length} elements` : 'Empty'}
                              </p>
                            </div>
                          )}
                          
                          {/* Overlay for element count */}
                          {size.elements.length > 0 && (
                            <div className="absolute top-2 right-2 bg-black bg-opacity-75 text-white text-xs px-2 py-1 rounded">
                              {size.elements.length} elements
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Size Info */}
                      <div className="mb-4">
                        <div className="flex items-center justify-between mb-2">
                          <h3 className="font-semibold text-gray-900">{size.name}</h3>
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(getCompletionStatus(size))}`}>
                            {getCompletionStatus(size)}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600">{size.width} × {size.height}</p>
                        <p className="text-xs text-gray-500">Modified: {formatDate(size.lastModified)}</p>
                      </div>

                      {/* Actions */}
                      <div className="flex space-x-2">
                        <button
                          onClick={() => onNavigateToBannerEditor(client.id, bannerSet.id, size.id)}
                          className="flex-1 flex items-center justify-center space-x-1 px-3 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 transition-colors"
                        >
                          <Edit size={14} />
                          <span>Edit</span>
                        </button>
                        
                        {size.elements.length > 0 && bannerSet.dataFeedArray.length > 0 && (
                          <button
                            onClick={() => handlePreviewSize(size)}
                            className="px-3 py-2 border border-gray-300 text-gray-700 text-sm rounded hover:bg-gray-50 transition-colors"
                          >
                            <Eye size={14} />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}

        {/* Empty State */}
        {bannerSet.sizes.length === 0 && (
          <div className="text-center py-12">
            <Grid className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No banner sizes</h3>
            <p className="mt-1 text-sm text-gray-500">
              Get started by adding your first custom banner size.
            </p>
            <button
              onClick={() => setShowAddBannerSize(true)}
              className="mt-4 flex items-center space-x-2 mx-auto px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Plus size={16} />
              <span>Add Banner Size</span>
            </button>
          </div>
        )}

        {/* Preview Modal for Individual Size */}
        {showPreviewModal && previewSize && (
          <PreviewModal
            isOpen={showPreviewModal}
            onClose={() => {
              setShowPreviewModal(false);
              setPreviewSize(null);
            }}
            elements={previewSize.elements}
            size={previewSize}
            dataFeedArray={bannerSet.dataFeedArray}
          />
        )}

        {/* Banner Set Preview Modal */}
        <BannerSetPreviewModal
          isOpen={showBannerSetPreview}
          onClose={() => setShowBannerSetPreview(false)}
          bannerSet={bannerSet}
        />

        {/* Banner Set Export Modal */}
        <BannerSetExportModal
          isOpen={showBannerSetExport}
          onClose={() => setShowBannerSetExport(false)}
          bannerSet={bannerSet}
        />

        {/* Platform Export Modal */}
        <PlatformExportModal
          isOpen={showPlatformExport}
          onClose={() => setShowPlatformExport(false)}
          bannerSet={bannerSet}
        />

        {/* Banner Deployment Modal */}
        <BannerDeploymentModal
          isOpen={showDeploymentModal}
          onClose={() => setShowDeploymentModal(false)}
          bannerSet={bannerSet}
          clientId={client.id}
          clientName={client.name}
          onUpdateBannerSet={onUpdateBannerSet}
        />

        {/* Add Banner Size Modal */}
        <AddBannerSizeModal
          isOpen={showAddBannerSize}
          onClose={() => setShowAddBannerSize(false)}
          onAddBannerSize={handleAddBannerSize}
          existingSizes={bannerSet.sizes}
        />
      </div>
    </div>
  );
};